import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Send, ShieldCheck } from "lucide-react";
import "../styles/whatsappButton.css";

const WHATSAPP_PHONE = "919289774500";

const QUICK_TOPICS = [
  {
    title: "🪔 Nitya Seva & Gotra Sankalpa",
    text: "Hare Krishna Prabhuji! I would like guidance on enrolling our family for monthly Nitya Seva with Gotra & Nakshatra sankalpa at ISKCON Delhi.",
  },
  {
    title: "🎂 Special Occasion Puja (Birthday / Anniversary)",
    text: "Hare Krishna! We want to sponsor a special Rajbhog Aarti & Archana on an upcoming family milestone. Please share available dates and slots.",
  },
  {
    title: "🍲 Annadaan & Sadhu Bhojan Sponsorship",
    text: "Hare Krishna! I wish to sponsor hot Prasadam distribution (Food For Life / Sadhu Seva) at Sri Sri Radha Parthasarathi Mandir. Please provide the details.",
  },
  {
    title: "📜 80G Tax Exemption & Payment Support",
    text: "Hare Krishna! I have a question regarding my recent online donation receipt / 80G tax exemption certificate. Kindly assist.",
  },
  {
    title: "🔔 Darshan, Aarti Timings & Mandir Visits",
    text: "Hare Krishna! Could you please share today's Darshan schedule, Rajbhog Aarti timings, and guidelines for visiting the temple at East of Kailash?",
  },
];

export default function WhatsAppButton() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [hasBottomBar, setHasBottomBar] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  // Check if a bottom sticky donation bar is present in DOM
  useEffect(() => {
    const checkBottomBar = () => {
      const stickyBar = document.querySelector(".ns2-sticky-bar");
      setHasBottomBar(Boolean(stickyBar));
    };

    checkBottomBar();
    const interval = setInterval(checkBottomBar, 500);
    window.addEventListener("scroll", checkBottomBar, { passive: true });

    // Auto-dismiss tooltip after 8 seconds
    const timer = setTimeout(() => {
      setShowTooltip(false);
    }, 8000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("scroll", checkBottomBar);
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleSend = (text) => {
    const messageToSend =
      text ||
      customMessage ||
      "Hare Krishna! I would like to inquire about Nitya Seva at ISKCON Delhi.";
    const encoded = encodeURIComponent(messageToSend);
    const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Container ── */}
      <div className={`wa-container ${hasBottomBar ? "has-bottom-bar" : ""}`}>
        {/* Interactive Speech Bubble Tooltip */}
        {!isOpen && showTooltip && (
          <div className="wa-tooltip">
            <button
              type="button"
              onClick={() => setShowTooltip(false)}
              className="wa-tooltip-close"
              aria-label="Dismiss message"
            >
              <X size={14} />
            </button>
            <div className="wa-tooltip-content">
              <span className="wa-tooltip-indicator" />
              <div>
                <p className="wa-tooltip-title">
                  Mandir Seva Sahayata • Online
                </p>
                <p className="wa-tooltip-text">
                  Hare Krishna! Gotra sankalpa, puja booking ya mandir darshan
                  ke liye humse WhatsApp par juden.
                </p>
              </div>
            </div>
            <div className="wa-tooltip-tail" />
          </div>
        )}

        {/* WhatsApp Round Floating Button */}
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            setShowTooltip(false);
          }}
          aria-label="Chat with ISKCON Delhi Seva Pujari on WhatsApp"
          className="wa-floating-btn"
        >
          {isOpen ? (
            <X size={24} />
          ) : (
            <>
              {/* WhatsApp SVG Icon */}
              <svg viewBox="0 0 24 24" className="wa-icon-svg">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
              </svg>
              {/* Online Green Pulsing Beacon */}
              <span className="wa-online-beacon">
                <span className="wa-beacon-ping" />
                <span className="wa-beacon-dot" />
              </span>
            </>
          )}
        </button>
      </div>

      {/* ── Luxury WhatsApp Concierge Drawer / Modal ── */}
      {isOpen && (
        <div className={`wa-drawer ${hasBottomBar ? "has-bottom-bar" : ""}`}>
          {/* Header Banner */}
          <div className="wa-drawer-header">
            <div className="wa-header-left">
              <div className="wa-avatar-wrap">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/images/iskcon.png`}
                  alt="ISKCON Delhi Logo"
                  className="wa-avatar-img"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
              <div>
                <h4 className="wa-header-title">ISKCON Delhi Seva Sahayata</h4>
                <div className="wa-header-status">
                  <span className="wa-status-dot" />
                  <span className="wa-status-text">
                    Sevaks Online • Fast Support
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="wa-drawer-close-btn"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          {/* Devotional Greeting & Quick Actions */}
          <div className="wa-drawer-body">
            {/* Priest Message Bubble */}
            <div className="wa-greeting-box">
              <p className="wa-greeting-heading">
                Dandavat Pranam! 🙏 Welcome to ISKCON Delhi
              </p>
              <p className="wa-greeting-sub">
                Sri Sri Radha Parthasarathi Mandir welcomes you. How may our
                sevaks assist your auspicious family seva today? Select a query
                below or type your message:
              </p>
            </div>

            {/* Quick Topic Chips */}
            <div>
              <p className="wa-topics-label">
                ✨ Frequent Seva & Darshan Inquiries:
              </p>
              <div className="wa-topics-list">
                {QUICK_TOPICS.map((topic, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSend(topic.text)}
                    className="wa-topic-chip"
                  >
                    <span className="wa-topic-chip-text">{topic.title}</span>
                    <Send size={13} className="wa-topic-chip-icon" />
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Input */}
            <div className="wa-custom-input-box">
              <label className="wa-topics-label">
                ✍️ Or Write Your Personal Query / Prayer:
              </label>
              <textarea
                rows={2}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Type your question or special prayer request here (e.g. Booking Ekadashi Abhishek)..."
                className="wa-custom-textarea"
              />
            </div>
          </div>

          {/* Action Footer */}
          <div className="wa-drawer-footer">
            <div className="wa-trust-badge">
              <ShieldCheck size={14} />
              <span>Official Temple Helpline</span>
            </div>

            <button
              type="button"
              onClick={() => handleSend(customMessage)}
              className="wa-send-btn"
            >
              <span>Start WhatsApp Chat</span>
              <Send size={12} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
